import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getRedemptionHistory } from '../../services/simpleCompService';
import { getCurrentEmployee } from '../../services/pinAuthService';
import styles from './AllRedemptions.module.css';

type FilterType = 'all' | 'drink' | 'food';
type SortType = 'newest' | 'oldest';

export const AllRedemptions: React.FC = () => {
  const admin = getCurrentEmployee();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const [sort, setSort] = useState<SortType>('newest');
  const [limit, setLimit] = useState(50);

  const { data: redemptionsResult, isLoading } = useQuery({
    queryKey: ['all-redemptions', filter, sort, limit],
    queryFn: () => getRedemptionHistory(undefined, limit),
  });

  const redemptions = redemptionsResult?.data || [];

  // Filter redemptions
  const filteredRedemptions = redemptions.filter(r =>
    filter === 'all' || r.type === filter
  );

  // Sort redemptions
  const sortedRedemptions = [...filteredRedemptions].sort((a, b) => {
    const dateA = new Date(a.redeemed_at).getTime();
    const dateB = new Date(b.redeemed_at).getTime();
    return sort === 'newest' ? dateB - dateA : dateA - dateB;
  });

  const formatRelativeTime = (dateString: string) => {
    const now = new Date();
    const then = new Date(dateString);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Calculate stats
  const drinkCount = redemptions.filter(r => r.type === 'drink').length;
  const foodCount = redemptions.filter(r => r.type === 'food').length;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button onClick={() => navigate('/admin')} className={styles.backButton}>
          BACK
        </button>
        <h1>All Redemptions</h1>
        <p className={styles.welcome}>Hi, {admin?.name?.split(' ')[0]}</p>
      </div>

      {/* Stats bar */}
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{redemptions.length}</span>
          <span className={styles.statLabel}>Total</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{drinkCount}</span>
          <span className={styles.statLabel}>Drinks</span>
        </div>
        <div className={styles.statItem}>
          <span className={styles.statValue}>{foodCount}</span>
          <span className={styles.statLabel}>Food</span>
        </div>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.filterGroup}>
          <label>Filter:</label>
          <div className={styles.filterButtons}>
            <button
              className={filter === 'all' ? styles.activeButton : styles.filterButton}
              onClick={() => setFilter('all')}
            >
              ALL
            </button>
            <button
              className={filter === 'drink' ? styles.activeButton : styles.filterButton}
              onClick={() => setFilter('drink')}
            >
              DRINKS
            </button>
            <button
              className={filter === 'food' ? styles.activeButton : styles.filterButton}
              onClick={() => setFilter('food')}
            >
              FOOD
            </button>
          </div>
        </div>

        <div className={styles.sortGroup}>
          <label>Sort:</label>
          <div className={styles.sortButtons}>
            <button
              className={sort === 'newest' ? styles.activeButton : styles.sortButton}
              onClick={() => setSort('newest')}
            >
              NEWEST
            </button>
            <button
              className={sort === 'oldest' ? styles.activeButton : styles.sortButton}
              onClick={() => setSort('oldest')}
            >
              OLDEST
            </button>
          </div>
        </div>

        <div className={styles.limitGroup}>
          <label>Show:</label>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className={styles.limitSelect}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="500">500</option>
          </select>
        </div>
      </div>

      {/* Redemption list */}
      {isLoading ? (
        <div className={styles.loading}>Loading redemptions...</div>
      ) : sortedRedemptions.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No redemptions found</p>
        </div>
      ) : (
        <div className={styles.redemptionList}>
          {sortedRedemptions.map((redemption) => (
            <div key={redemption.id} className={styles.redemptionItem}>
              <div className={styles.redemptionInfo}>
                <span className={styles.employeeName}>
                  {redemption.employee?.name || 'Unknown'}
                </span>
                <span className={styles.compType} data-type={redemption.type}>
                  {redemption.type.toUpperCase()}
                </span>
              </div>
              <div className={styles.redemptionMeta}>
                <span className={styles.redeemedAt} title={formatDate(redemption.redeemed_at)}>
                  {formatRelativeTime(redemption.redeemed_at)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
